import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api';

// Pricing (effective Feb 2026):
//   • Standard programs: $199 enrollment + $149 mid-term + $149 final = $497
//     collected via Stripe; remainder of stated tuition deferred under ETA-671.
//   • Flat $99 programs (no exams, no deferred): GED, Cyber Security, Forklift OSHA.
const ONLINE_COURSES = [
  { value: 'ONLINE_CNA_PREP', id: 'cna-prep', label: 'Certified Medical Assistant (CMA) — Online · $199 + $149 + $149' },
  { value: 'ONLINE_LPN_PREP', id: 'lpn-prep', label: 'Practical Nurse Tech Apprenticeship — Online · $199 + $149 + $149' },
  { value: 'ONLINE_PHLEBOTOMY_PREP', id: 'phlebotomy-prep', label: 'Phlebotomy Technician — Online · $199 + $149 + $149' },
  { value: 'ONLINE_EMT_PREP', id: 'emt-prep', label: 'Emergency Medical Technician (EMT) — Online · $199 + $149 + $149' },
  { value: 'ONLINE_HOMELAND_PREP', id: 'homeland-security-prep', label: 'Master Homeland Security Specialist — Online · $199 + $149 + $149' },
  { value: 'ONLINE_MED_TERM_PREP', id: 'medical-terminology-prep', label: 'Medical Terminology — Online · $199 + $149 + $149' },
  { value: 'ONLINE_MEDICAL_AIDE_PREP', id: 'medical-aide-prep', label: 'Medical Aide — Outpatient Clinic Apprenticeship · $199 + $149 + $149' },
  { value: 'ONLINE_GED_PREP', id: 'ged-prep', label: 'GED Adult Learning — Online · $99 flat' },
  // Feb 2026 additions
  { value: 'ONLINE_CYBERSECURITY_PREP', id: 'cybersecurity-prep', label: 'Cyber Security — Online · $99 flat' },
  { value: 'ONLINE_FORKLIFT_OSHA', id: 'forklift-osha-prep', label: 'Forklift OSHA Certification — Online · $99 flat' },
  { value: 'ONLINE_MEDICAL_BILLING_CODING', id: 'medical-billing-coding-prep', label: 'Medical Billing & Coding — Online · $199 + $149 + $149' },
  { value: 'ONLINE_DOCTOR_NATURAL_HEALTH', id: 'doctor-natural-health', label: 'Doctor of Natural Health — Online (Doctorate) · $199 + $149 + $149' },
  { value: 'ONLINE_DOCTOR_HOLISTIC_WELLNESS', id: 'doctor-holistic-wellness', label: 'Doctor of Holistic Wellness — Online (Doctorate) · $199 + $149 + $149' },
  { value: 'ONLINE_HERBAL_HEALTH_PRACTITIONER', id: 'herbal-health-practitioner', label: 'Herbal Health Practitioner Certification — Online (Doctorate) · $199 + $149 + $149' },
  { value: 'ONLINE_INTEGRATIVE_WELLNESS_CONSULTANT', id: 'integrative-wellness-consultant', label: 'Integrative Wellness Consultant — Online (Doctorate) · $199 + $149 + $149' },
  { value: 'ONLINE_DOCTOR_VITAL_HEALTH_SCIENCES', id: 'doctor-vital-health-sciences', label: 'Doctor of Vital Health Sciences — Practitioner — Online (Doctorate) · $199 + $149 + $149' },
];

const Register = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectedProgram = searchParams.get('program') || 'ONLINE_CNA_PREP';

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    phone: '',
    program: preselectedProgram,
    role: 'student',
    apprentice_id: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/register', {
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        phone: formData.phone,
        role: formData.role,
        program: formData.program,
        apprentice_id: formData.apprentice_id,
      });

      onLogin(response.data.user, response.data.access_token);

      const courseMap = Object.fromEntries(ONLINE_COURSES.map((c) => [c.value, c.id]));
      const courseId = courseMap[formData.program] || 'cna-prep';
      // Mandatory next step: sign the ETA-671 Apprenticeship Agreement.
      setTimeout(() => {
        navigate(`/apprenticeship-agreement?next=${encodeURIComponent(`/online-courses?enroll=${courseId}`)}`);
      }, 100);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" data-testid="register-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
              <img
                src="/usdol_seal.png"
                alt="U.S. Department of Labor Seal"
                data-testid="register-usdol-seal"
                style={{ width: 72, height: 72, objectFit: 'contain' }}
              />
            </div>
            <h1 className="auth-title">Create Your Account</h1>
            <p className="auth-subtitle" style={{ marginBottom: 4 }}>
              Online Apprenticeship Training · Self-Paced
            </p>
            <p className="auth-subtitle" style={{ fontSize: '0.78rem', color: '#64748b' }}>
              <strong>$99 program enrollment fee</strong> · $99 mid-term &amp; $99 final exams · stated tuition deferred under ETA-671 · <strong>GED: $99 flat</strong>
            </p>
          </div>

          {error && (
            <div className="alert alert-error" data-testid="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your full name"
                required
                data-testid="fullname-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your email"
                required
                data-testid="email-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="form-input"
                placeholder="(901) 555-0123"
                required
                data-testid="phone-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Online Course</label>
              <select
                name="program"
                value={formData.program}
                onChange={handleChange}
                className="form-input"
                data-testid="program-select"
              >
                {ONLINE_COURSES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label} — $99
                  </option>
                ))}
              </select>

              <div
                style={{
                  marginTop: '12px',
                  padding: '14px 16px',
                  background: '#ecfdf5',
                  borderRadius: '8px',
                  border: '1px solid #bbf7d0',
                }}
                data-testid="online-course-notice"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '1rem' }}>🎓</span>
                  <span style={{ fontWeight: 600, color: '#166534' }}>Self-Paced Online Apprenticeship</span>
                </div>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#166534', lineHeight: 1.5 }}>
                  Instant access after payment. Reading modules, end-of-module quizzes, and a
                  certificate of completion after all modules are done and admin-approved.
                </p>
                <ul
                  style={{
                    margin: '10px 0 0',
                    padding: '0 0 0 18px',
                    fontSize: '0.78rem',
                    color: '#065f46',
                    lineHeight: 1.6,
                  }}
                >
                  <li><strong>$99</strong> program-enrollment fee (all programs)</li>
                  <li>Mid-term &amp; final exams: <strong>$99 each</strong></li>
                  <li>Stated tuition <strong>deferred</strong> under signed ETA-671 Apprenticeship Agreement</li>
                  <li><strong>GED:</strong> single flat <strong>$99</strong> with no deferred tuition</li>
                  <li>After Register you'll sign an <strong>Apprenticeship Application &amp; Tuition Agreement</strong> before course access unlocks</li>
                </ul>
              </div>

              {/* USDOL Registered Apprenticeship banner */}
              <div
                style={{
                  marginTop: '12px',
                  padding: '12px 16px',
                  background: '#ecfdf5',
                  borderRadius: '8px',
                  border: '1px solid #6ee7b7',
                }}
                data-testid="apprenticeship-disclaimer-register"
              >
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#065f46', lineHeight: 1.5 }}>
                  <strong>USDOL Registered Apprenticeship.</strong> Every program is administered
                  under the U.S. Department of Labor Registered Apprenticeship framework by
                  MedCare Nursing Institute &amp; Training Academy Inc. through the Office of
                  Workforce Development.
                </p>
              </div>
            </div>

            {/* No Refund Policy Notice */}
            <div
              style={{
                marginTop: '0',
                marginBottom: '16px',
                padding: '12px 16px',
                background: '#fef2f2',
                borderRadius: '8px',
                border: '1px solid #fecaca',
              }}
              data-testid="no-refund-policy-register"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontSize: '1rem' }}>⚠️</span>
                <span style={{ fontWeight: 600, color: '#dc2626', fontSize: '0.875rem' }}>NO REFUND POLICY</span>
              </div>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#991b1b', lineHeight: 1.4 }}>
                All online course fees (enrollment, mid-term, final exam) are non-refundable.
                By registering, you acknowledge and agree to this policy.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Apprentice ID (Optional)</label>
              <input
                type="text"
                name="apprentice_id"
                value={formData.apprentice_id}
                onChange={handleChange}
                className="form-input"
                placeholder="DOL Apprentice ID (if assigned)"
                data-testid="apprentice-id-input"
              />
              <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
                Enter your USDOL-assigned Apprentice ID if you have one. This can be added later.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="Create a password"
                required
                data-testid="password-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="form-input"
                placeholder="Confirm your password"
                required
                data-testid="confirm-password-input"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg"
              style={{ width: '100%' }}
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating Account...' : 'Create Account & Continue'}
            </button>
          </form>

          <div className="auth-footer">
            Already have an account? <Link to="/login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
